import { parseWeldTxt, type WeldTxtParseResult } from '../weldTxtParser'
import type { MachineType } from '../weldTxtParser'

export interface TelemetryFileParse {
  file: File
  parse: WeldTxtParseResult
}

function weldNumbersFromParse(parse: WeldTxtParseResult): string[] {
  if (parse.distinctWeldNumbers.length > 0) {
    return parse.distinctWeldNumbers.map((w) => w.trim()).filter(Boolean)
  }
  const single = (parse.weldNumber ?? '').trim()
  return single ? [single] : []
}

/**
 * Group telemetry TXT files by parsed `Weld #`. Files with multiple distinct
 * weld numbers in one file are assigned to every matching group.
 */
export function groupFilesByTelemetryWeldNumber(
  entries: TelemetryFileParse[]
): Map<string, File[]> {
  const groups = new Map<string, File[]>()

  for (const { file, parse } of entries) {
    const ids = weldNumbersFromParse(parse)
    if (ids.length === 0) {
      const unassigned = groups.get('') ?? []
      unassigned.push(file)
      groups.set('', unassigned)
      continue
    }
    for (const id of ids) {
      const list = groups.get(id) ?? []
      if (!list.includes(file)) {
        list.push(file)
      }
      groups.set(id, list)
    }
  }

  return groups
}

export function detectTelemetryWeldNumbers(
  entries: TelemetryFileParse[]
): { weldIds: string[]; ambiguous: boolean } {
  const ids = new Set<string>()
  for (const { parse } of entries) {
    for (const id of weldNumbersFromParse(parse)) {
      ids.add(id)
    }
  }
  const weldIds = Array.from(ids).filter(Boolean).sort()
  return { weldIds, ambiguous: weldIds.length > 1 }
}

export async function readTelemetryFileParses(
  files: File[],
  machineType: MachineType = 'p600z'
): Promise<TelemetryFileParse[]> {
  const results: TelemetryFileParse[] = []
  for (const file of files) {
    const text = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        resolve(typeof reader.result === 'string' ? reader.result : '')
      }
      reader.onerror = () => reject(reader.error ?? new Error(`Failed to read ${file.name}`))
      reader.readAsText(file)
    })
    results.push({ file, parse: parseWeldTxt(text, machineType) })
  }
  return results
}
